import {Injectable} from '@angular/core';

/**
 * Represents an entry in the IndexedDB.
 */
export interface IndexedDatabaseEntry {
    /**
     * Optional unique identifier for the entry.
     */
    id?: number;
    /**
     * The timestamp representing when the entry was created.
     */
    createdAt: number;
    /**
     * The measurement data stored as a key-value pair record.
     */
    data: Record<string, unknown>;
}

/**
 * Service for interacting with IndexedDB to store and retrieve measurement data.
 * This class provides methods for initializing the database, adding entries,
 * fetching all entries, and clearing the database.
 */
@Injectable({
    providedIn: 'root'
})
export class IndexedMeasurementDatabase {

    private readonly name = 'indexed_measurement_database';
    private readonly tableName = 'measurements';
    private readonly version = 1;
    /**
     * The current IDBDatabase instance, or null if not yet initialized.
     */
    public database: IDBDatabase | null = null;

    /**
     * Initializes the IndexedDB.
     * Opens a connection to the database and creates the object store if it doesn't already exist.
     *
     * @returns A promise that resolves when the database is successfully initialized.
     */
    public async init(): Promise<void> {
        if (this.database) {
            return;
        }

        this.database = await new Promise<IDBDatabase>((resolve, reject) => {
            const request = indexedDB.open(this.name, this.version);

            request.onupgradeneeded = () => {
                const database = request.result;

                if (!database.objectStoreNames.contains(this.tableName)) {
                    database.createObjectStore(this.tableName, { keyPath: 'id', autoIncrement: true });
                }
            };

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Gets the current IDBDatabase instance, ensuring it is initialized.
     *
     * @returns A promise that resolves to the IDBDatabase instance.
     * @throws Error if the database could not be initialized.
     */
    private async getDb(): Promise<IDBDatabase> {
        if (!this.database) {
            await this.init();
        }

        if (!this.database) {
            throw new Error('Database could not be initialized.');
        }

        return this.database;
    }

    /**
     * Adds a new entry to the database.
     *
     * @param entry - The entry to be added to the database.
     * @returns A promise that resolves when the entry is successfully added.
     */
    public async add(entry: IndexedDatabaseEntry): Promise<void> {
        const database = await this.getDb();

        await new Promise<void>((resolve, reject) => {
            const transaction = database.transaction(this.tableName, 'readwrite');
            const store = transaction.objectStore(this.tableName);

            store.put(entry);

            transaction.oncomplete = () => resolve();
            transaction.onerror = () => reject(transaction.error);
        });
    }

    /**
     * Retrieves all entries from the database.
     *
     * @returns A promise that resolves to an array of all IndexedDatabaseEntry objects.
     */
    public async getAll(): Promise<IndexedDatabaseEntry[]> {
        const database = await this.getDb();

        return await new Promise<IndexedDatabaseEntry[]>((resolve, reject) => {
            const transaction = database.transaction(this.tableName, 'readonly');
            const store = transaction.objectStore(this.tableName);
            const request = store.getAll();

            request.onsuccess = () => resolve(request.result as IndexedDatabaseEntry[]);
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Deletes an entry from the database by its ID.
     *
     * @param id - The unique identifier of the entry to delete.
     * @returns A promise that resolves when the entry is successfully deleted.
     */
    public async delete(id: string): Promise<void> {
        const database = await this.getDb();

        await new Promise<void>((resolve, reject) => {
            const transaction = database.transaction(this.tableName, 'readwrite');
            const store = transaction.objectStore(this.tableName);

            store.delete(id);

            transaction.oncomplete = () => resolve();
            transaction.onerror = () => reject(transaction.error);
        });
    }

    /**
     * Clears all entries from the database.
     *
     * @returns A promise that resolves when the object store is successfully cleared.
     */
    public async clear(): Promise<void> {
        const database = await this.getDb();

        await new Promise<void>((resolve, reject) => {
            const transaction = database.transaction(this.tableName, 'readwrite');
            const store = transaction.objectStore(this.tableName);

            store.clear();

            transaction.oncomplete = () => resolve();
            transaction.onerror = () => reject(transaction.error);
        });
    }
}
